import { IPreciseLocation } from '~/client/graph'

/**
 * A workflow location's report snapshot: the two map screenshots persisted on
 * `preciseLocation.imageUrl` / `overviewImageUrl` and embedded in PDF reports.
 *
 * The URLs are a cached projection of everything the location mini-map shows —
 * pin camera, georeference, markups. They stay valid only until one of those
 * changes, and every code path that changes one must invalidate through
 * `clearSnapshotUrls` so the save-time capture refreshes them. The callers of
 * `clearSnapshotUrls` ARE the invalidation inventory: add new sources there,
 * never as ad-hoc null writes.
 */

type CoordinateFields = Pick<IPreciseLocation, 'latitude' | 'longitude'>

/**
 * True only when the record carries an actual user-placed pin. An image-only
 * record (report screenshots without coordinates) must NOT read as a pin —
 * use this instead of truthiness.
 */
export function hasPreciseCoordinates<T extends CoordinateFields>(
  preciseLocation: T | null | undefined,
): preciseLocation is T {
  return preciseLocation?.latitude != null && preciseLocation?.longitude != null
}

/** Both screenshots present: the snapshot is complete and a save may skip recapture. */
export function hasCompleteSnapshot(
  preciseLocation:
    | Pick<IPreciseLocation, 'imageUrl' | 'overviewImageUrl'>
    | null
    | undefined,
): boolean {
  return !!preciseLocation?.imageUrl && !!preciseLocation?.overviewImageUrl
}

/**
 * Invalidate a location value's snapshot in place (MobX-friendly). Explicit
 * null (not undefined) so the cleared fields serialize into the mutation
 * payload and clear the server-side record. Safe on any field value — no-ops
 * unless the value carries a preciseLocation record.
 */
export function clearSnapshotUrls(
  location: { preciseLocation?: IPreciseLocation | null } | null | undefined,
): void {
  const pl = location?.preciseLocation
  if (!pl) {
    return
  }
  pl.imageUrl = null
  pl.overviewImageUrl = null
}

export type SnapshotAction = 'capture' | 'skip' | 'clear-stale' | 'none'

/**
 * The save-time snapshot decision, as pure data:
 *  - 'capture'     — the location renders a pin (placed or resolved) and the
 *                    snapshot is missing or incomplete
 *  - 'skip'        — a complete snapshot exists; every change that could stale
 *                    it clears the URLs first, so presence means validity
 *  - 'clear-stale' — a record exists but the location no longer renders a pin;
 *                    only trusted when location data has settled, so a
 *                    transiently-empty store cannot wipe good images
 *  - 'none'        — nothing to capture and nothing trustworthy to clear
 */
export function decideSnapshotAction(ctx: {
  hasPin: boolean
  isResolvable: boolean
  isLocationDataSettled: boolean
  hasRecord: boolean
  hasCompleteSnapshot: boolean
}): SnapshotAction {
  if (!ctx.hasPin && !ctx.isResolvable) {
    return ctx.hasRecord && ctx.isLocationDataSettled ? 'clear-stale' : 'none'
  }
  return ctx.hasCompleteSnapshot ? 'skip' : 'capture'
}

export interface ISnapshotUrls {
  imageUrl: string | null
  overviewImageUrl: string | null
}

/**
 * Both URLs always come from ONE run: the images are one camera at two zooms, so pairing a fresh
 * half with the previous run's can print an overview tighter than the precise shot it places.
 */
export function mergeSnapshotResult(
  current:
    | Pick<IPreciseLocation, 'imageUrl' | 'overviewImageUrl'>
    | null
    | undefined,
  result: { imageUrl?: string; overviewImageUrl?: string },
): ISnapshotUrls {
  const fresh = {
    imageUrl: result.imageUrl ?? null,
    overviewImageUrl: result.overviewImageUrl ?? null,
  }
  if (fresh.imageUrl && fresh.overviewImageUrl) {
    return fresh
  }
  return hasCompleteSnapshot(current)
    ? {
        imageUrl: current.imageUrl,
        overviewImageUrl: current.overviewImageUrl,
      }
    : fresh
}
